import { gql } from '@apollo/client';
import { Box, Button, Divider, Grid, Radio, Typography } from '@mui/material';
import { ContextMenu } from 'components/common/ContextMenu';
import { CustomDialog } from 'components/common/CustomDialog';
import { IconCustomSmiley } from 'components/icons/components/custom/IconCustomSmiley';
import { IconLinearSort } from 'components/icons/components/linear/IconLinearSort';
import { NestedFiltersMenuView } from 'features/nestedFilters';
import {
  SocialMediaListeningFilterByDateRangeButton,
  SocialMediaListeningPostDetailView,
  useBrandDetails,
} from 'features/socialMediaListening';
import { SocialPostCardView } from 'features/socialPost';
import {
  GetSocialPostsByBrandSentimentCategoryFiltersInput,
  GetSocialPostsByBrandSentimentCategorySortBy,
  Sentiment,
  SocialPostFragmentSocialPostCardViewFragmentDoc,
  SortOrder,
  useGetSocialPostsForSlaBrandSentimentSocialPostDialogViewQuery,
} from 'graphql/generated';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';

// eslint-disable-next-line
gql`
  query GetSocialPostsForSLABrandSentimentSocialPostDialogView(
    $filters: GetSocialPostsByBrandSentimentCategoryFiltersInput!
    $sortBy: GetSocialPostsByBrandSentimentCategorySortByInput
    $after: String
    $take: Int
  ) {
    getSocialPostsByBrandSentimentCategory(
      filters: $filters
      sortBy: $sortBy
      after: $after
      take: $take
    ) {
      data {
        ...SocialPostFragmentSocialPostCardView
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      meta {
        totalCount
        totalEngagement
        totalViewCount
      }
    }
  }
  ${SocialPostFragmentSocialPostCardViewFragmentDoc}
`;

const SortFieldMap = {
  [GetSocialPostsByBrandSentimentCategorySortBy.TotalEngagement]:
    'Total Engagement',
  [GetSocialPostsByBrandSentimentCategorySortBy.PlayCount]: 'Play count',
  [GetSocialPostsByBrandSentimentCategorySortBy.LikeCount]: 'Like Count',
};

const sentimentOptions = [
  {
    label: 'Positive',
    value: Sentiment.Positive,
  },
  {
    label: 'Negative',
    value: Sentiment.Negative,
  },
  {
    label: 'Neutral',
    value: Sentiment.Neutral,
  },
];

type Props = Partial<GetSocialPostsByBrandSentimentCategoryFiltersInput> & {
  brandId: string;
  onClose: () => void;
};

export const SLABrandSentimentSocialPostsDialogView = ({
  brandId,
  onClose,
  dateRange: defaultDateRange,
  mediums,
  ...restFilters
}: Props) => {
  const [sortKey, setSortKey] = useState(
    GetSocialPostsByBrandSentimentCategorySortBy.TotalEngagement,
  );

  const { brand } = useBrandDetails({ brandId });
  const [currentPostId, setCurrentPostId] = useState('');
  const [dateRange, setDateRange] = useState(
    defaultDateRange ?? {
      startDate: moment().subtract(7, 'days').toDate(),
      endDate: moment().toDate(),
    },
  );
  const [sentiment, setSentiment] = useState<Sentiment>();

  const filters: GetSocialPostsByBrandSentimentCategoryFiltersInput = useMemo(
    () => ({
      ...restFilters,
      brandId,
      brandSentimentCategoryId: '',
      dateRange,
      sentiments: sentiment ? [sentiment] : [],
      mediums: mediums ?? [],
    }),
    [brandId, dateRange, sentiment, restFilters, mediums],
  );

  const {
    data: socialPostsData,
    fetchMore: fetchMoreSocialPosts,
    loading: socialPostsLoading,
  } = useGetSocialPostsForSlaBrandSentimentSocialPostDialogViewQuery({
    variables: {
      filters,
      sortBy: {
        field: sortKey,
        order: SortOrder.Desc,
      },
    },
    skip: !brandId,
    fetchPolicy: 'cache-and-network',
  });

  const socialPosts =
    socialPostsData?.getSocialPostsByBrandSentimentCategory.data;

  useEffect(() => {
    if (defaultDateRange) {
      setDateRange(defaultDateRange);
    }
  }, [defaultDateRange]);

  const stats = useMemo(() => {
    return [
      {
        title: 'Total post count',
        value: formatBigNumber(
          socialPostsData?.getSocialPostsByBrandSentimentCategory.meta
            .totalCount ?? 0,
        ),
      },
      {
        title: 'Total view count',
        value: formatBigNumber(
          socialPostsData?.getSocialPostsByBrandSentimentCategory.meta
            .totalViewCount ?? 0,
        ),
      },
      {
        title: 'Total engagement',
        value: formatBigNumber(
          socialPostsData?.getSocialPostsByBrandSentimentCategory.meta
            .totalEngagement ?? 0,
        ),
      },
    ];
  }, [socialPostsData]);

  const handleBackClick = () => {
    if (currentPostId) {
      setCurrentPostId('');
    } else {
      onClose();
    }
  };

  return (
    <CustomDialog
      open
      onClose={onClose}
      titleBreadcrumbs={['Sentiment over time']}
      onBackClick={handleBackClick}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {!currentPostId && (
          <Box p={3} flexGrow={1} overflow="auto">
            <InfiniteScroll
              loadMore={() => {
                fetchMoreSocialPosts({
                  variables: {
                    filters,
                    sortBy: {
                      field: sortKey,
                      order: SortOrder.Desc,
                    },
                    after:
                      socialPostsData?.getSocialPostsByBrandSentimentCategory
                        .pageInfo.endCursor,
                  },
                  updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;
                    return {
                      getSocialPostsByBrandSentimentCategory: {
                        ...fetchMoreResult.getSocialPostsByBrandSentimentCategory,
                        data: [
                          ...prev.getSocialPostsByBrandSentimentCategory.data,
                          ...fetchMoreResult
                            .getSocialPostsByBrandSentimentCategory.data,
                        ],
                      },
                    };
                  },
                });
              }}
              hasMore={
                socialPostsData?.getSocialPostsByBrandSentimentCategory.pageInfo
                  .hasNextPage
              }
              threshold={50}
              useWindow={false}
            >
              <Box
                display="flex"
                alignItems="flex-start"
                gap={2}
                mb={3}
                justifyContent="space-between"
                width="100%"
                sx={{
                  p: 4,
                }}
              >
                <Box display="flex" flexDirection="column">
                  <Typography variant="headline-lg" mr={4}>
                    {brand?.name}
                  </Typography>

                  <Typography
                    variant="body-xl"
                    fontWeight={600}
                    mt={2}
                    color={theme.colors?.utility[700]}
                  >
                    {
                      socialPostsData?.getSocialPostsByBrandSentimentCategory
                        .meta.totalCount
                    }{' '}
                    posts
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <NestedFiltersMenuView
                    items={[
                      {
                        type: 'single-select',
                        key: 'sentiments',
                        sortSelectedOptionFirst: false,
                        options: sentimentOptions,
                      },
                    ]}
                    values={{
                      sentiments: {
                        value: sentimentOptions.find(
                          (option) => option.value === sentiment,
                        ),
                      },
                    }}
                    onChange={(values) => {
                      // @ts-ignore
                      const sentimentValue = values.sentiments.value.value;
                      if (sentimentValue === sentiment) {
                        setSentiment(undefined);
                      } else {
                        setSentiment(sentimentValue);
                      }
                    }}
                    componentsProps={{
                      trigger: {
                        text:
                          sentimentOptions.find(
                            (option) => option.value === sentiment,
                          )?.label ?? 'All Sentiment',
                        Icon: IconCustomSmiley,
                        sx: {
                          py: theme.spacing(2),
                          backgroundColor: theme.colors?.primary.parchment,
                        },
                      },
                    }}
                  />
                  <SocialMediaListeningFilterByDateRangeButton
                    onChange={({ dateRange }) => {
                      if (dateRange[0] && dateRange[1]) {
                        setDateRange({
                          startDate: dateRange[0],
                          endDate: dateRange[1],
                        });
                      }
                    }}
                    selectedDateRange={dateRange}
                    componentsProps={{
                      sx: {
                        backgroundColor: theme.colors?.primary.parchment,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ m: 4 }}>
                <Grid
                  container
                  spacing={2}
                  mb={3}
                  sx={{
                    backgroundColor: theme.colors?.primary.parchment,
                    borderRadius: 2,
                    p: 4,
                  }}
                >
                  {stats.map((stat, index) => (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        borderLeft: {
                          md:
                            index > 0
                              ? `1px solid ${theme.colors?.utility[400]}`
                              : 'none',
                        },
                      }}
                    >
                      <Box
                        flexDirection="column"
                        borderRadius={2}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        sx={{
                          pl: {
                            md: index > 0 ? 4 : 0,
                          },
                        }}
                      >
                        <Typography
                          variant="body-xl"
                          color={theme.colors?.utility[800]}
                          fontWeight={600}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          {stat.title}
                        </Typography>
                        <Typography
                          fontSize={theme.spacing(8)}
                          variant="headline-lg"
                          color={theme.colors?.utility[900]}
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider
                sx={{
                  borderBottom: `2px solid rgba(35, 6, 3, 0.05)`,
                  my: 4,
                }}
              />

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                m={4}
                mb={2}
              >
                <Typography variant="headline-sm">Posts</Typography>
                <ContextMenu
                  sx={{
                    '& .MuiMenuItem-root': {
                      p: 0,
                      color: theme.colors?.primary.black,
                    },
                  }}
                  options={Object.entries(SortFieldMap).map(([key, value]) => ({
                    renderOption: () => (
                      <Box display="flex" alignItems="center">
                        <Radio
                          checked={
                            sortKey ===
                            GetSocialPostsByBrandSentimentCategorySortBy[key]
                          }
                        />
                        <Typography variant="body-lg">{value}</Typography>
                      </Box>
                    ),
                    onClick: () => {
                      setSortKey(
                        GetSocialPostsByBrandSentimentCategorySortBy[key],
                      );
                    },
                  }))}
                  renderButton={() => (
                    <Button
                      sx={{
                        backgroundColor: theme.colors?.primary.parchment,
                        color: theme.colors?.primary.black,
                        borderRadius: theme.spacing(2),
                        display: 'flex',
                        gap: 2,
                        p: theme.spacing(1.5, 2),
                        alignItems: 'center',
                        '&:hover': {
                          backgroundColor: theme.colors?.utility[275],
                        },
                      }}
                      disableRipple={false}
                    >
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          backgroundColor: 'rgba(35, 6, 3, 0.05)',
                          p: theme.spacing(1),
                          borderRadius: theme.spacing(1),
                        }}
                      >
                        <IconLinearSort size={16} />
                      </Box>
                      <Typography fontWeight={500} variant="body-xl">
                        Sort by
                      </Typography>
                    </Button>
                  )}
                />
              </Box>

              {!socialPostsLoading && (
                <Box sx={{ m: 4 }}>
                  {socialPosts?.length ? (
                    <Grid container spacing={2}>
                      {socialPosts?.map((post) => {
                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            lg={2.4}
                            key={post.id}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                              }}
                              onClick={() => setCurrentPostId(post.id)}
                            >
                              <SocialPostCardView socialPost={post} />
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography
                        variant="body-xl"
                        fontWeight={600}
                        color={theme.colors?.utility[800]}
                      >
                        No posts found
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </InfiniteScroll>
          </Box>
        )}
        {currentPostId && (
          <Box
            px={12}
            sx={{
              backgroundColor: theme.colors?.primary.parchment,
              minHeight: theme.spacing(100),
              overflow: 'auto',
            }}
          >
            <Box
              p={4}
              mt={8}
              sx={{
                backgroundColor: theme.colors?.primary.white,
                borderRadius: theme.spacing(4),
              }}
            >
              <SocialMediaListeningPostDetailView
                brandId={brandId}
                socialPostId={currentPostId}
                componentProps={{
                  modalView: true,
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </CustomDialog>
  );
};
